import { useState, useEffect, useCallback } from 'react';
import { proveedoresApi, auditoriaProveedoresApi } from '../../lib/api';
import { useRegisterCmdKCommands } from '../../contexts/CmdKContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { validarRutChileno, consultarSII } from '../../lib/rut';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { CECPStatusChip } from '../../components/ui/CECPStatusChip';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import { toast as siiToast } from 'sonner';
import {
  Truck, Plus, Pencil, Trash2, Search, RefreshCw, Download,
  CheckCircle2, XCircle, Mail, Phone, MapPin, FileText,
  ChevronLeft, ChevronRight, CircleDollarSign, ExternalLink, Printer, FileSpreadsheet, ShieldCheck,
  ShoppingCart, Receipt,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { exportToCSV, printTable } from '../../lib/printUtils';

const emptyProveedor = () => ({
  nombre: '', rut: '', email: '', email_cecp: '', telefono: '', ciudad: '', direccion: '',
  giro: '', condicion_pago: '', condicion_pago_codigo: '', activo: true, nota: '',
  forma_pago: '', dias_plazo: 0, contacto_principal: '',
});

const FORMAS_PAGO = [
  'Contado',
  'Transferencia',
  'Cheque 30 días',
  'Cheque 60 días',
  'Cheque 90 días',
  'Otro',
];

export default function ProveedoresList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [activoFiltro, setActivoFiltro] = useState('todos'); // todos | activos | inactivos
  const [fuenteFiltro, setFuenteFiltro] = useState('todos'); // todos | alegra | manual
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(emptyProveedor());
  const [saving, setSaving] = useState(false);

  const [importDialog, setImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Iter 286.o — catálogo condiciones_pago (13 vigentes) para el dropdown
  const [condicionesPago, setCondicionesPago] = useState([]);
  // Iter 373 — discrepancias pendientes de la auditoría nocturna RUT/razón social
  const [auditoriaPendientes, setAuditoriaPendientes] = useState(0);
  useEffect(() => {
    auditoriaProveedoresApi.conteo().then((r) => setAuditoriaPendientes(r.data?.pendientes || 0)).catch(() => {});
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const res = await proveedoresApi.getCondicionesPago
          ? await proveedoresApi.getCondicionesPago()
          : await (await import('../../lib/api')).default.get('/condiciones-pago-crud', { params: { solo_nuevas: true } });
        setCondicionesPago(res.data || []);
      } catch { setCondicionesPago([]); }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q: search, page, page_size: pageSize };
      if (activoFiltro === 'activos') params.activo = true;
      else if (activoFiltro === 'inactivos') params.activo = false;
      if (fuenteFiltro !== 'todos') params.fuente = fuenteFiltro;
      const res = await proveedoresApi.list(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al cargar proveedores', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize, activoFiltro, fuenteFiltro]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  // Carlos (10/05/2026): imprimir y exportar Proveedores
  const imprimirProveedores = () => {
    if (!items.length) { toast.warning('No hay proveedores para imprimir'); return; }
    printTable({
      title: 'Listado de Proveedores',
      subtitle: `${items.length} proveedores · ${search ? `Filtro: "${search}"` : 'Todos'} · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total proveedores', value: total ?? items.length },
        { label: 'Activos', value: items.filter(p => p.activo !== false).length, color: '#047857' },
        { label: 'Inactivos', value: items.filter(p => p.activo === false).length, color: '#b91c1c' },
        { label: 'Bloqueados conciliación', value: items.filter(p => p.bloqueado_por_conciliacion).length, color: '#b45309' },
      ],
      columns: [
        { key: 'rut', label: 'RUT', mono: true },
        { key: 'nombre', label: 'Razón Social' },
        { key: 'giro', label: 'Giro' },
        { key: 'email', label: 'Email Compras' },
        { key: 'telefono', label: 'Teléfono' },
        { key: 'ciudad', label: 'Ciudad' },
        { key: 'condicion_pago', label: 'Cond. pago', align: 'center' },
        { key: 'forma_pago', label: 'Forma pago', align: 'center' },
        { key: 'activo', label: 'Estado', align: 'center', format: (v) => v === false ? 'INACTIVO' : 'ACTIVO' },
      ],
      rows: items,
    });
  };

  const exportarProveedoresExcel = () => {
    if (!items.length) { toast.warning('No hay proveedores para exportar'); return; }
    exportToCSV({
      filename: 'proveedores',
      title: 'CHIRINO STEEL · Listado de Proveedores',
      summary: [
        { label: 'Total proveedores', value: items.length },
        { label: 'Filtro búsqueda', value: search || 'Todos' },
        { label: 'Activos', value: items.filter(p => p.activo !== false).length },
        { label: 'Inactivos', value: items.filter(p => p.activo === false).length },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'rut', label: 'RUT' },
        { key: 'nombre', label: 'Razón Social' },
        { key: 'giro', label: 'Giro' },
        { key: 'email', label: 'Email Compras' },
        { key: 'email_cecp', label: 'Email Estados de Cuenta' },
        { key: 'telefono', label: 'Teléfono' },
        { key: 'direccion', label: 'Dirección' },
        { key: 'ciudad', label: 'Ciudad' },
        { key: 'contacto_principal', label: 'Contacto principal' },
        { key: 'condicion_pago', label: 'Condición pago' },
        { key: 'forma_pago', label: 'Forma pago' },
        { key: 'dias_plazo', label: 'Días plazo' },
        { key: 'activo', label: 'Estado', format: (v) => v === false ? 'INACTIVO' : 'ACTIVO' },
        { key: 'bloqueado_por_conciliacion', label: 'Bloqueado conciliación', format: (v) => v ? 'SÍ' : '' },
        { key: 'cecp_status', label: 'Estado CECP' },
        { key: 'nota', label: 'Nota' },
      ],
      rows: items,
    });
  };

  const openNew = () => { setForm(emptyProveedor()); setEditing('new'); };
  const openEdit = (p) => {
    setForm({
      nombre: p.nombre || '', rut: p.rut || '', email: p.email || '',
      email_cecp: p.email_cecp || '',
      telefono: p.telefono || '', ciudad: p.ciudad || '',
      direccion: p.direccion || '', giro: p.giro || '',
      condicion_pago: p.condicion_pago || '',
      condicion_pago_codigo: p.condicion_pago_codigo || '',
      activo: p.activo !== false, nota: p.nota || '',
      forma_pago: p.forma_pago || '',
      dias_plazo: p.dias_plazo || 0,
      contacto_principal: p.contacto_principal || '',
    });
    setEditing(p.id);
  };

  // Punto 6 Carlos: drill-down Saldo CxP
  const [cxpDialog, setCxpDialog] = useState(null); // { proveedor, facturas, total_saldo }
  const [cxpLoading, setCxpLoading] = useState(false);
  const navigate = useNavigate();

  const openCxpDetalle = async (p) => {
    setCxpLoading(true);
    setCxpDialog({ proveedor: p, facturas: [], total_saldo: 0, count: 0 });
    try {
      const res = await api.get(`/proveedores/${p.id}/cxp-detalle`);
      setCxpDialog({ proveedor: p, ...res.data });
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error cargando detalle CxP', variant: 'destructive' });
      setCxpDialog(null);
    } finally {
      setCxpLoading(false);
    }
  };

  const save = async () => {
    if (!form.nombre.trim()) {
      toast({ title: 'Nombre requerido', variant: 'destructive' });
      return;
    }
    if (form.rut && form.rut.trim() && !validarRutChileno(form.rut)) {
      toast({ title: 'RUT inválido: revisa el dígito verificador (módulo 11)', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await proveedoresApi.create(form);
        toast({ title: 'Proveedor creado' });
      } else {
        await proveedoresApi.update(editing, form);
        toast({ title: 'Proveedor actualizado' });
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (p) => {
    try {
      await proveedoresApi.toggleActivo(p.id);
      toast({ title: p.activo ? 'Proveedor desactivado' : 'Proveedor activado' });
      await load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar al proveedor "${p.nombre}"?\nEsta acción no se puede deshacer.`)) return;
    try {
      await proveedoresApi.remove(p.id);
      toast({ title: 'Proveedor eliminado' });
      await load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al eliminar', variant: 'destructive' });
    }
  };

  const ejecutarImport = async () => {
    setImporting(true);
    try {
      const res = await proveedoresApi.importarAlegra({ dry_run: false, solo_con_email: false });
      setImportResult(res.data.stats);
      toast({
        title: `Importación OK: ${res.data.stats.creados} nuevos, ${res.data.stats.actualizados} actualizados`,
      });
      await load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al importar', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  // ────────────────── Cmd+K: comandos contextuales ──────────────────
  useRegisterCmdKCommands(
    'proveedores-list',
    'Proveedores',
    [
      { id: 'prov-new', label: 'Crear nuevo proveedor', sublabel: 'Manual (sin Alegra)',
        icon: 'Plus', keywords: 'crear nuevo proveedor alta', run: openNew },
      { id: 'prov-importar', label: 'Importar proveedores desde Alegra',
        sublabel: 'Sincroniza nuevos + actualiza existentes', icon: 'RefreshCw',
        keywords: 'importar alegra sync proveedores',
        run: () => setImportDialog(true), enabled: !importing },
      { id: 'prov-bloqueados', label: 'Ir a Proveedores Bloqueados (CECP)',
        sublabel: 'Conciliación proveedores', icon: 'ArrowRight',
        keywords: 'bloqueados bloqueo cecp conciliacion',
        run: () => navigate('/contabilidad/conciliacion-proveedores') },
      { id: 'prov-f-activos', label: 'Filtrar: solo activos', icon: 'Filter',
        run: () => { setActivoFiltro('activos'); setPage(1); },
        enabled: activoFiltro !== 'activos' },
      { id: 'prov-f-inactivos', label: 'Filtrar: solo inactivos', icon: 'Filter',
        run: () => { setActivoFiltro('inactivos'); setPage(1); },
        enabled: activoFiltro !== 'inactivos' },
      { id: 'prov-f-alegra', label: 'Filtrar: solo desde Alegra', icon: 'Filter',
        run: () => { setFuenteFiltro('alegra'); setPage(1); },
        enabled: fuenteFiltro !== 'alegra' },
      { id: 'prov-f-manual', label: 'Filtrar: solo creados manuales', icon: 'Filter',
        run: () => { setFuenteFiltro('manual'); setPage(1); },
        enabled: fuenteFiltro !== 'manual' },
      { id: 'prov-clear', label: 'Limpiar filtros', icon: 'RefreshCw',
        run: () => {
          setActivoFiltro('todos'); setFuenteFiltro('todos');
          setSearch(''); setSearchInput(''); setPage(1);
        },
        enabled: activoFiltro !== 'todos' || fuenteFiltro !== 'todos' || !!search },
      { id: 'prov-refresh', label: 'Refrescar listado', icon: 'RefreshCw', run: load },
    ],
    [activoFiltro, fuenteFiltro, search, importing, load, navigate],
  );

  return (
    <div className="p-4 space-y-3" data-testid="proveedores-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="w-6 h-6" />
            Proveedores
          </h1>
          <p className="text-sm text-muted-foreground">
            {total} {total === 1 ? 'proveedor' : 'proveedores'}
            {search ? ` · Filtro: "${search}"` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/compras/proveedores/auditoria-nombres')} className="relative" title="Auditoría nocturna de RUT y razón social vs DTE/RCV" data-testid="btn-auditoria-nombres">
            <ShieldCheck className="w-4 h-4 mr-1" /> Auditoría RUT / Razón Social
            {auditoriaPendientes > 0 && (
              <Badge variant="destructive" className="ml-2 px-1.5 py-0 text-[10px]" data-testid="badge-auditoria-pendientes">{auditoriaPendientes}</Badge>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
          <Button variant="outline" size="sm" onClick={imprimirProveedores} title="Imprimir listado" data-testid="btn-print-proveedores">
            <Printer className="w-4 h-4 mr-1" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={exportarProveedoresExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-proveedores">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setImportDialog(true); setImportResult(null); }} data-testid="btn-importar-alegra">
            <Download className="w-4 h-4 mr-1" /> Importar desde Alegra
          </Button>
          <Button size="sm" onClick={openNew} data-testid="btn-nuevo-proveedor">
            <Plus className="w-4 h-4 mr-1" /> Nuevo Proveedor
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-3">
          <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[260px]">
              <Label className="text-xs">Buscar (nombre, rut, email)</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Ej: COMERCIAL, 76xxx..."
                  className="pl-8"
                  data-testid="input-buscar"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Estado</Label>
              <select
                value={activoFiltro}
                onChange={(e) => { setActivoFiltro(e.target.value); setPage(1); }}
                className="h-9 text-sm border rounded px-2 bg-white dark:bg-slate-900"
                data-testid="select-activo"
              >
                <option value="todos">Todos</option>
                <option value="activos">Solo activos</option>
                <option value="inactivos">Solo inactivos</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Fuente</Label>
              <select
                value={fuenteFiltro}
                onChange={(e) => { setFuenteFiltro(e.target.value); setPage(1); }}
                className="h-9 text-sm border rounded px-2 bg-white dark:bg-slate-900"
                data-testid="select-fuente"
              >
                <option value="todos">Todas</option>
                <option value="alegra">Solo Alegra</option>
                <option value="manual">Solo Manual</option>
              </select>
            </div>
            <Button type="submit" size="sm" data-testid="btn-buscar">
              <Search className="w-4 h-4 mr-1" /> Buscar
            </Button>
            {(search || activoFiltro !== 'todos' || fuenteFiltro !== 'todos') && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => { setSearchInput(''); setSearch(''); setActivoFiltro('todos'); setFuenteFiltro('todos'); setPage(1); }}
                data-testid="btn-limpiar"
              >
                Limpiar
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Truck className="w-10 h-10 mx-auto opacity-30 mb-2" />
              <p className="text-sm">Sin proveedores. Crea uno con "Nuevo Proveedor" o importa desde Alegra.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 dark:bg-slate-900 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">RUT</th>
                  <th className="px-3 py-2 text-left">Nombre</th>
                  <th className="px-3 py-2 text-left">Contacto</th>
                  <th className="px-3 py-2 text-left">Cond. Pago</th>
                  <th className="px-3 py-2 text-center" title="OCs aprobadas no anuladas en últimos 90 días">OCs Activas</th>
                  <th className="px-3 py-2 text-right" title="Suma total OCs aprobadas en mes en curso">Gasto Mes</th>
                  <th className="px-3 py-2 text-right" title="Saldo actual CxP (alegra_bills pendientes) — click para detalle">Saldo CxP</th>
                  <th className="px-3 py-2 text-center">Fuente</th>
                  <th className="px-3 py-2 text-center">Estado</th>
                  <th className="px-3 py-2 text-center w-32">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => {
                  const inactivo = p.activo === false;
                  return (
                    <tr
                      key={p.id}
                      className={`border-t hover:bg-blue-50/50 dark:hover:bg-blue-950/20 ${inactivo ? 'opacity-60' : ''}`}
                      data-testid={`row-proveedor-${p.id}`}
                    >
                      <td className="px-3 py-2 font-mono text-xs">{p.rut || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2 font-medium">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{p.nombre}</span>
                          <CECPStatusChip rut={p.rut} proveedorId={p.id} />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs space-y-0.5">
                        {p.email && (
                          <div className="flex items-center gap-1 text-muted-foreground" title="Correo de Compras / Cotizaciones">
                            <Mail className="w-3 h-3 text-emerald-600" /> {p.email}
                            <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full uppercase">Compras</span>
                          </div>
                        )}
                        {p.email_cecp && p.email_cecp !== p.email && (
                          <div className="flex items-center gap-1 text-muted-foreground" title="Correo de Cobranza / Estados de Cuenta (CECP)">
                            <Mail className="w-3 h-3 text-blue-500" /> {p.email_cecp}
                            <span className="text-[9px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full uppercase">Cobranza</span>
                          </div>
                        )}
                        {p.telefono && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="w-3 h-3" /> {p.telefono}
                          </div>
                        )}
                        {p.ciudad && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="w-3 h-3" /> {p.ciudad}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {p.condicion_pago || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center" data-testid={`prov-ocs-activas-${p.id}`}>
                        {p.ocs_activas_count > 0 ? (
                          <span className="inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-md bg-indigo-100 text-indigo-700 font-bold text-xs">
                            {p.ocs_activas_count}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs" data-testid={`prov-gasto-mes-${p.id}`}>
                        {p.gasto_mes_actual > 0 ? (
                          <span className="text-emerald-700 font-semibold">${(p.gasto_mes_actual || 0).toLocaleString('es-CL')}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs" data-testid={`prov-saldo-cxp-${p.id}`}>
                        {p.saldo_cxp_total > 0 ? (
                          <button
                            type="button"
                            onClick={() => openCxpDetalle(p)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 font-semibold tabular-nums cursor-pointer"
                            title={`${p.cxp_pendientes_count || 0} facturas pendientes — click para ver detalle`}
                          >
                            <CircleDollarSign className="w-3 h-3" />
                            ${(p.saldo_cxp_total || 0).toLocaleString('es-CL')}
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {p.alegra_id || p.fuente === 'alegra' ? (
                          <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-300">Alegra</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">Manual</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {inactivo ? (
                          <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-300">
                            <XCircle className="w-3 h-3 mr-0.5" /> Inactivo
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-300">
                            <CheckCircle2 className="w-3 h-3 mr-0.5" /> Activo
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="icon" variant="ghost" className="h-7 w-7"
                            onClick={() => openEdit(p)}
                            title="Editar"
                            data-testid={`btn-edit-${p.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="icon" variant="ghost"
                            className={`h-7 w-7 ${inactivo ? 'text-emerald-600' : 'text-amber-600'}`}
                            onClick={() => toggleActivo(p)}
                            title={inactivo ? 'Activar' : 'Desactivar'}
                            data-testid={`btn-toggle-${p.id}`}
                          >
                            {inactivo ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                          </Button>
                          <Button
                            size="icon" variant="ghost" className="h-7 w-7 text-red-600"
                            onClick={() => remove(p)}
                            title="Eliminar (solo si no tiene OCs)"
                            data-testid={`btn-delete-${p.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-muted-foreground">
            Página {page} de {totalPages} · {total} {total === 1 ? 'resultado' : 'resultados'}
          </p>
          <div className="flex items-center gap-1">
            <Button
              size="sm" variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="btn-prev-page"
            >
              <ChevronLeft className="w-4 h-4" /> Anterior
            </Button>
            <Button
              size="sm" variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="btn-next-page"
            >
              Siguiente <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Dialog Crear/Editar */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="dialog-proveedor">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nuevo Proveedor' : 'Editar Proveedor'}</DialogTitle>
            <DialogDescription>
              {editing === 'new'
                ? 'Crea manualmente un proveedor que no esté en Alegra.'
                : 'Modifica los datos del proveedor. Los campos vacíos al importar desde Alegra se completarán automáticamente la próxima vez.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nombre / Razón Social *</Label>
              <Input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                data-testid="input-nombre"
              />
            </div>
            <div>
              <Label>RUT</Label>
              <Input
                value={form.rut}
                onChange={(e) => setForm({ ...form, rut: e.target.value })}
                placeholder="76xxx-x"
                data-testid="input-rut"
              />
              {form.rut && form.rut.trim() && !validarRutChileno(form.rut) && (
                <p className="text-xs text-red-500 mt-1" data-testid="rut-error">
                  Dígito verificador inválido (módulo 11)
                </p>
              )}
              {/* Iter 292.a (Carlos, feb-2026) — Link SII, mismo patrón que GenerarOCPage. */}
              <button
                type="button"
                onClick={() => consultarSII(form.rut, siiToast)}
                className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-1"
                data-testid="btn-consultar-sii"
              >
                <ExternalLink className="w-3 h-3" /> Consultar situación tributaria en SII
              </button>
            </div>
            <div>
              <Label>Condición de Pago</Label>
              {/* Iter 286.o — dropdown desde catálogo `condiciones_pago` */}
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.condicion_pago_codigo || ''}
                onChange={(e) => {
                  const codigo = e.target.value;
                  const cp = condicionesPago.find(c => c.codigo === codigo);
                  setForm({
                    ...form,
                    condicion_pago_codigo: codigo,
                    condicion_pago: cp ? cp.nombre : '',
                    dias_plazo: cp ? (cp.dias_plazo || 0) : 0,
                  });
                }}
                data-testid="input-cond-pago"
              >
                <option value="">— Seleccionar —</option>
                {condicionesPago.map((cp) => (
                  <option key={cp.codigo} value={cp.codigo}>
                    {cp.nombre}{cp.dias_plazo > 0 ? ` (${cp.dias_plazo} días)` : ''}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Los días de plazo se toman automáticamente desde <b>Configuración &gt; Condiciones de Pago</b>. Ya no es editable manualmente.
              </p>
            </div>
            <div>
              <Label>Forma de Pago</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.forma_pago || ''}
                onChange={(e) => setForm({ ...form, forma_pago: e.target.value })}
                data-testid="input-forma-pago"
              >
                <option value="">— Seleccionar —</option>
                {FORMAS_PAGO.map((fp) => <option key={fp} value={fp}>{fp}</option>)}
              </select>
            </div>
            <div>
              <Label>Contacto Principal</Label>
              <Input
                value={form.contacto_principal}
                onChange={(e) => setForm({ ...form, contacto_principal: e.target.value })}
                placeholder="Nombre / cargo"
                data-testid="input-contacto-principal"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
                  <ShoppingCart className="w-3 h-3" /> Compras
                </span>
                Correo de Compras / Cotizaciones
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="compras@proveedor.cl"
                data-testid="input-email"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Se usa para enviar <b>Órdenes de Compra</b> y <b>solicitudes de cotización</b>. Se actualiza automáticamente cuando le envías una OC o cotización.</p>
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
                  <Receipt className="w-3 h-3" /> Cobranza
                </span>
                Correo de Cobranza / Estados de Cuenta (CECP)
              </Label>
              <Input
                type="email"
                value={form.email_cecp}
                onChange={(e) => setForm({ ...form, email_cecp: e.target.value })}
                placeholder="cobranza@proveedor.cl"
                data-testid="input-email-cecp"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Canal distinto para solicitar <b>estados de cuenta</b>. El módulo de Compras <b>nunca</b> lo usa ni lo modifica.</p>
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                data-testid="input-telefono"
              />
            </div>
            <div>
              <Label>Ciudad</Label>
              <Input
                value={form.ciudad}
                onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
                data-testid="input-ciudad"
              />
            </div>
            <div>
              <Label>Giro</Label>
              <Input
                value={form.giro}
                onChange={(e) => setForm({ ...form, giro: e.target.value })}
                data-testid="input-giro"
              />
            </div>
            <div className="col-span-2">
              <Label>Dirección</Label>
              <Input
                value={form.direccion}
                onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                data-testid="input-direccion"
              />
            </div>
            <div className="col-span-2">
              <Label>Nota</Label>
              <Input
                value={form.nota}
                onChange={(e) => setForm({ ...form, nota: e.target.value })}
                placeholder="Opcional"
                data-testid="input-nota"
              />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Switch
                checked={form.activo}
                onCheckedChange={(v) => setForm({ ...form, activo: v })}
                data-testid="switch-activo"
              />
              <Label>Proveedor activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Importar Alegra */}
      <Dialog open={importDialog} onOpenChange={(o) => !o && setImportDialog(false)}>
        <DialogContent className="max-w-xl" data-testid="dialog-importar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Importar Proveedores desde Alegra
            </DialogTitle>
            <DialogDescription>
              Sincroniza los contactos tipo <strong>provider</strong> desde Alegra a la lista local.
              Los proveedores ya existentes solo recibirán datos faltantes (no se sobreescriben campos manuales).
            </DialogDescription>
          </DialogHeader>

          {!importResult ? (
            <div className="space-y-3 text-sm">
              <div className="p-3 rounded bg-blue-50 border border-blue-200 text-blue-900">
                <FileText className="w-4 h-4 inline mr-1" />
                Se buscarán todos los contactos de Alegra con tipo <code>provider</code>.
                El proceso puede tardar según la cantidad de proveedores.
              </div>
              <ul className="text-xs space-y-1 text-muted-foreground list-disc pl-5">
                <li>Match por <strong>RUT</strong> (identificacion). No duplica.</li>
                <li>Solo completa campos vacíos (no pisa datos manuales).</li>
                <li>Marca los nuevos con <code>fuente=alegra</code>.</li>
              </ul>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <Card><CardContent className="p-3">
                  <div className="text-muted-foreground text-xs">Leídos de Alegra</div>
                  <div className="text-xl font-bold">{importResult.leidos_alegra}</div>
                </CardContent></Card>
                <Card className="bg-emerald-50"><CardContent className="p-3">
                  <div className="text-emerald-700 text-xs">Creados</div>
                  <div className="text-xl font-bold text-emerald-700">{importResult.creados}</div>
                </CardContent></Card>
                <Card className="bg-blue-50"><CardContent className="p-3">
                  <div className="text-blue-700 text-xs">Actualizados</div>
                  <div className="text-xl font-bold text-blue-700">{importResult.actualizados}</div>
                </CardContent></Card>
                <Card className="bg-slate-50"><CardContent className="p-3">
                  <div className="text-slate-600 text-xs">Sin cambios / sin RUT</div>
                  <div className="text-sm font-bold">
                    Sin cambios: {importResult.sin_cambios}<br/>
                    Sin RUT: {importResult.sin_rut_skip}
                  </div>
                </CardContent></Card>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialog(false)}>
              {importResult ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!importResult && (
              <Button
                onClick={ejecutarImport}
                disabled={importing}
                data-testid="btn-confirm-import"
              >
                {importing ? 'Importando…' : 'Iniciar Importación'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Punto 6: Drill-down Saldo CxP */}
      <Dialog open={!!cxpDialog} onOpenChange={(o) => !o && setCxpDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-cxp-detalle">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CircleDollarSign className="w-5 h-5 text-red-600" />
              Saldo CxP — {cxpDialog?.proveedor?.nombre || ''}
            </DialogTitle>
            <DialogDescription>
              Facturas pendientes (Alegra saldo &gt; 0) asociadas a este proveedor. Vencidas destacadas en rojo.
            </DialogDescription>
          </DialogHeader>

          {cxpLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : cxpDialog && cxpDialog.count === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              Este proveedor no tiene facturas pendientes en Alegra.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 p-3 rounded-md bg-red-50 border border-red-200">
                <div className="text-xs text-red-900">
                  <strong>{cxpDialog?.count || 0}</strong> facturas pendientes
                </div>
                <div className="text-lg font-bold text-red-700 tabular-nums" data-testid="cxp-total-saldo">
                  ${(cxpDialog?.total_saldo || 0).toLocaleString('es-CL')}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs mt-2">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="p-2 text-left">Folio</th>
                      <th className="p-2 text-left">Emisión</th>
                      <th className="p-2 text-left">Vencimiento</th>
                      <th className="p-2 text-right">Monto Total</th>
                      <th className="p-2 text-right">Saldo</th>
                      <th className="p-2 text-center">Días Atraso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cxpDialog?.facturas || []).map((f) => (
                      <tr
                        key={f.alegra_id || f.folio}
                        className={`border-t ${f.vencida ? 'bg-red-50 text-red-700' : 'hover:bg-muted/30'}`}
                      >
                        <td className="p-2 font-mono">{f.folio || '—'}</td>
                        <td className="p-2">{f.fecha || '—'}</td>
                        <td className="p-2 font-medium">{f.fecha_vencimiento || '—'}</td>
                        <td className="p-2 text-right tabular-nums">${(f.monto_total || 0).toLocaleString('es-CL')}</td>
                        <td className="p-2 text-right tabular-nums font-semibold">${(f.saldo || 0).toLocaleString('es-CL')}</td>
                        <td className="p-2 text-center">
                          {f.dias_atraso > 0 ? (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-red-600 text-white font-bold text-[10px]">
                              {f.dias_atraso}d
                            </span>
                          ) : (
                            <span className="text-muted-foreground">al día</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCxpDialog(null)} data-testid="btn-close-cxp">
              Cerrar
            </Button>
            <Button
              onClick={() => {
                const nombre = cxpDialog?.proveedor?.nombre || '';
                setCxpDialog(null);
                navigate(`/contabilidad/flujo-caja?cxp_proveedor=${encodeURIComponent(nombre)}`);
              }}
              data-testid="btn-ir-cxp"
            >
              <ExternalLink className="w-4 h-4 mr-1" /> Ir a CxP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
